import { PATH_MAX_X, STEPS_TOTAL } from './gameConfig.js';
import { COLORS, type ColorType } from './types.js';

/**
 * Mulberry32 — deterministic PRNG so client+server can replay an identical path
 * given the same seed. Server emits the seed in `match.found`; clients use it
 * to draw the same wall.
 */
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return function rng(): number {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Continuous-line zig-zag path: x flows as a smooth random walk with
 * momentum so consecutive holds connect into a hand-drawn squiggle (not
 * isolated dots). Wall slaps reverse direction sharply. The result reads
 * as ONE line that sweeps across, hits a wall, sweeps back — mirrors the
 * user's hand-drawn reference.
 */
export function genPath(n: number = STEPS_TOTAL, seed?: number): number[] {
  const INIT_KICK = 32;
  const NUDGE = 24;
  const DAMP = 0.86;
  const WALL_BOUNCE = 0.9;
  const rng = seed != null ? mulberry32(seed) : Math.random;
  const path: number[] = [];
  let x = 0;
  let v = (rng() - 0.5) * INIT_KICK;
  for (let i = 0; i < n; i++) {
    v += (rng() - 0.5) * NUDGE;
    v *= DAMP;
    x += v;
    if (x > PATH_MAX_X) {
      x = PATH_MAX_X;
      v = -Math.abs(v) * WALL_BOUNCE;
    }
    if (x < -PATH_MAX_X) {
      x = -PATH_MAX_X;
      v = Math.abs(v) * WALL_BOUNCE;
    }
    path.push(Math.round(x));
  }
  return path;
}

/**
 * Vertical position of each hold along the wall, in pixels from the wall
 * bottom. Spacing VARIES per hold (some tight, some loose) but the total
 * height stays at n * STEP_PX so the wall has a stable size. Same seed
 * gives the same Y-profile, so both clients agree on hold positions.
 */
export function genYOffsets(
  n: number = STEPS_TOTAL,
  step_px: number,
  seed?: number,
): number[] {
  const rng = seed != null ? mulberry32(seed + 7919) : Math.random;
  const raw: number[] = [];
  let sum = 0;
  // Pick per-hold spacing between 0.55× and 1.55× of the base STEP. Some
  // bunch up, others spread out — gives the climb an organic feel.
  for (let i = 0; i < n; i++) {
    const d = step_px * (0.55 + rng() * 1.0);
    raw.push(d);
    sum += d;
  }
  // Normalize so cumulative height matches n * STEP_PX exactly (the wall's
  // total Y budget stays constant; only the *distribution* changes).
  const scale = (n * step_px) / sum;
  const out: number[] = [];
  let acc = 0;
  for (let i = 0; i < n; i++) {
    acc += raw[i]! * scale;
    out.push(Math.round(acc));
  }
  return out;
}

/**
 * Random color sequence used by both lanes. The same seed produces the same
 * sequence so the race is fair — neither player gets an easier route.
 * Constraint: at most TWO consecutive holds may share a color (a pair is OK,
 * a triple is forbidden) — adds visual variety without making the rhythm
 * trivially predictable.
 */
export function randomSeq(n: number = STEPS_TOTAL, seed?: number): ColorType[] {
  const rng = seed != null ? mulberry32(seed + 1) : Math.random;
  const out: ColorType[] = [];
  let prev = -1;
  let prevPrev = -1;
  for (let i = 0; i < n; i++) {
    let pick: number;
    do {
      pick = Math.floor(rng() * 4);
      // Reject only when picking would make THREE in a row.
    } while (pick === prev && pick === prevPrev);
    prevPrev = prev;
    prev = pick;
    out.push(COLORS[pick]!);
  }
  return out;
}
